import { createClient } from '@supabase/supabase-js';
import type { User } from '@supabase/supabase-js';

// Get Supabase URL from environment variables
// If VITE_SUPABASE_URL is not set, construct it from VITE_SUPABASE_PROJECT_REF
const getSupabaseUrl = (): string => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (url) {
    return url;
  }
  
  const projectRef = import.meta.env.VITE_SUPABASE_PROJECT_REF;
  if (projectRef) {
    return `https://${projectRef}.supabase.co`;
  }
  
  throw new Error('Missing Supabase URL or Project Ref. Please set VITE_SUPABASE_URL or VITE_SUPABASE_PROJECT_REF in your .env file');
};

// Get Supabase anon key from environment variables
const getSupabaseAnonKey = (): string => {
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!key) {
    throw new Error('Missing VITE_SUPABASE_ANON_KEY. Please set it in your .env file');
  }
  return key;
};

// Create Supabase client with auth configuration
export const supabase = createClient(
  getSupabaseUrl(),
  getSupabaseAnonKey(),
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  }
);

// User ID management using localStorage
const ANONYMOUS_USER_ID_KEY = 'anonymous_user_id';

/**
 * Get or create anonymous user ID
 */
export const getAnonymousUserId = (): string => {
  let userId = localStorage.getItem(ANONYMOUS_USER_ID_KEY);
  
  if (!userId) {
    // Generate a simple UUID-like string
    userId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(ANONYMOUS_USER_ID_KEY, userId);
  }
  
  return userId;
};

/**
 * Clear anonymous user ID (useful for testing or reset)
 */
export const clearAnonymousUserId = (): void => {
  localStorage.removeItem(ANONYMOUS_USER_ID_KEY);
};

/**
 * Get user ID (authenticated user ID or anonymous user ID)
 * Priority: authenticated user > anonymous user
 */
export const getUserId = async (): Promise<{ userId: string; isAuthenticated: boolean }> => {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      return {
        userId: user.id,
        isAuthenticated: true,
      };
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error getting authenticated user:', error);
    }
  }
  
  // Fall back to anonymous user ID
  return {
    userId: getAnonymousUserId(),
    isAuthenticated: false,
  };
};

// 1.2.10: 创建专门用于远程 Storage 的 Supabase client
// 用于混合配置：其他服务使用本地，Storage 使用远程
const getRemoteStorageUrl = (): string | null => {
  const url = import.meta.env.VITE_REMOTE_STORAGE_URL;
  if (url) {
    return url;
  }
  
  const projectRef = import.meta.env.VITE_REMOTE_STORAGE_PROJECT_REF;
  if (projectRef) {
    return `https://${projectRef}.supabase.co`;
  }
  
  return null;
};

const getRemoteStorageAnonKey = (): string | null => {
  return import.meta.env.VITE_REMOTE_STORAGE_ANON_KEY || null;
};

// 创建远程 Storage client（如果配置了远程 Storage）
const remoteStorageUrl = getRemoteStorageUrl();
const remoteStorageKey = getRemoteStorageAnonKey();

export const remoteStorageClient = remoteStorageUrl && remoteStorageKey
  ? createClient(remoteStorageUrl, remoteStorageKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })
  : null;

// 获取 Storage client（优先使用远程，否则使用本地）
export const getStorageClient = () => {
  if (remoteStorageClient) {
    return remoteStorageClient;
  }
  return supabase;
};

